import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, WafChallengeError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_FORGOT_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, WafChallengeNotice, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/** `a-forgot` — starts a reset. Non-enumerating: identical response for a known and unknown
 * address (`identityRequestPasswordReset`, family rule A3). */
export function AForgot({ onRequested, onBackToSignIn }: { onRequested: (email: string) => void; onBackToSignIn: () => void }) {
  const [email, setEmail] = useState('')

  const request = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error, response } = await client.POST('/auth/password/forgot', { body: { email } })
      if (error) {
        if (response.status === 405) throw new WafChallengeError()
        throw new ApiProblemError(error as Problem)
      }
    },
    onSuccess: () => onRequested(email),
  })

  const problem = request.error instanceof ApiProblemError ? request.error.problem : undefined
  const wafChallenge = request.error instanceof WafChallengeError

  return (
    <AuthCard unitId={unitId('a-forgot')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_FORGOT_COPY.heading}</h3>
      <AuthCardLead>{A_FORGOT_COPY.lead}</AuthCardLead>

      <FieldStack>
        <Field label={A_FORGOT_COPY.emailLabel}>
          <input type="email" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </FieldStack>

      {wafChallenge && <WafChallengeNotice />}
      {problem && <AForgotRefusal problem={problem} />}

      <Button data-act={actionId('session.forgotPassword')} disabled={request.isPending || !email} onClick={() => request.mutate()}>
        {request.isPending ? 'Sending…' : A_FORGOT_COPY.submitButton}
      </Button>

      <Note>{A_FORGOT_COPY.nonEnumeratingNote}</Note>
      <p className="mt-14">
        <a href="#" data-act={actionId('nav.a-signin')} onClick={(e) => { e.preventDefault(); onBackToSignIn() }} className="text-link text-small">
          {A_FORGOT_COPY.backToSignInLabel}
        </a>
      </p>
    </AuthCard>
  )
}

function AForgotRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'VALIDATION_FAILED') {
    return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
  }
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
